import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { DimensionToken, EnumToken } from '@sleed/bizcommerce-api';
import { dimensionsApi } from '~/lib/api/client';
import { formToToken, type DimensionFormValues } from '~/lib/schemas/dimension';

export const dimensionKeys = {
	all: ['dimensions'] as const,
	list: () => [...dimensionKeys.all, 'list'] as const,
	types: () => [...dimensionKeys.all, 'types'] as const
};

export function useDimensionsList() {
	return useQuery<DimensionToken[]>({
		queryKey: dimensionKeys.list(),
		queryFn: async () => (await dimensionsApi.getAll()).data.items ?? []
	});
}

/** Dimension type lookup (Custom/Color/Size) for the type column + edit dropdown. */
export function useDimensionTypes() {
	return useQuery<EnumToken[]>({
		queryKey: dimensionKeys.types(),
		queryFn: async () => (await dimensionsApi.getDimensionTypes()).data.items ?? [],
		staleTime: 5 * 60 * 1000
	});
}

export function useDimensionMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: dimensionKeys.list() });
	const create = useMutation({
		mutationFn: async (form: DimensionFormValues) => (await dimensionsApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: DimensionFormValues) => (await dimensionsApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await dimensionsApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
